/** @jsxImportSource @emotion/react */
import React, { useMemo, useRef } from 'react'
import { css, Global } from '@emotion/react'
import { Select, TimePicker } from 'antd'
const { Option } = Select
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import Icon from 'src/components/Icon'
import type { TimeSlotRowProps } from './types'
import { COLUMN_WIDTHS } from './types'
import DayOfWeekSelector from './DayOfWeekSelector'
import {
  DAYS_OF_WEEK,
  RECURRENCE_TYPES,
} from '../../hooks/useSchedulerTimeSlots'

const TIME_SLOT_ROW_PICKER_CLASS = 'time-slot-row-picker-popup'

// Helper to format days for display
const formatDaysDisplay = (days: number[]): string => {
  if (!days || days.length === 0) {
    return '---'
  }
  if (days.length === 7) {
    return 'Everyday'
  }
  if (
    days.length === 5 &&
    days.includes(1) &&
    days.includes(2) &&
    days.includes(3) &&
    days.includes(4) &&
    days.includes(5)
  ) {
    return 'Weekdays'
  }
  if (days.length === 2 && days.includes(0) && days.includes(6)) {
    return 'Weekends'
  }
  return days
    .sort((a, b) => a - b)
    .map((d) => DAYS_OF_WEEK.find((day) => day.value === d)?.short || '')
    .join(', ')
}

// Helper to calculate duration
const calculateDuration = (startTime: string, endTime: string): string => {
  if (!startTime || !endTime) {
    return '00:00:00'
  }
  const [startH, startM] = startTime.split(':').map(Number)
  const [endH, endM] = endTime.split(':').map(Number)

  let diffMinutes = endH * 60 + endM - (startH * 60 + startM)
  if (diffMinutes < 0) {
    diffMinutes += 24 * 60
  } // Handle overnight

  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:00`
}

// Helper to format time for display (12hr format)
const formatTimeDisplay = (time24: string): string => {
  if (!time24) {
    return '00:00 AM'
  }
  const [hours, minutes] = time24.split(':').map(Number)
  const period = hours >= 12 ? 'PM' : 'AM'
  const hours12 = hours % 12 || 12
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`
}

// Helper to parse time from API format
const parseTimeFromSlot = (
  timeRanges: { start_time: string; end_time: string }[]
): { start: string; end: string } => {
  if (!timeRanges || timeRanges.length === 0) {
    return { start: '00:00', end: '00:00' }
  }
  // Use first time range
  const range = timeRanges[0]
  return {
    start: range.start_time?.slice(0, 5) || '00:00',
    end: range.end_time?.slice(0, 5) || '00:00',
  }
}

/**
 * Time slot row component - handles both display and edit modes
 */
const TimeSlotRow: React.FC<TimeSlotRowProps> = ({
  timeSlot,
  index,
  isEditing,
  editState,
  divisions,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onEditStateChange,
  loading,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const divisionsSelectRef = useRef<any>(null)
  const slotNumber = (index + 1).toString().padStart(4, '0')
  const times = useMemo(
    () => parseTimeFromSlot(timeSlot.time_ranges),
    [timeSlot.time_ranges]
  )

  // Common text style
  const textStyles = css`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20.3px;
    color: ${colors.NEUTRAL_30};
    overflow: hidden;
    text-overflow: ellipsis;
  `

  // TimePicker styles
  const timePickerStyles = css`
    height: 40px;
    background-color: ${colors.SECONDARY_400};
    border: 1px solid ${colors.SECONDARY_200};
    border-radius: 8px;

    input {
      color: ${colors.NEUTRAL_0} !important;
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 500;
    }

    &:hover,
    &.ant-picker-focused {
      border-color: ${colors.PRIMARY_300};
    }

    .ant-picker-suffix {
      color: ${colors.NEUTRAL_50};
    }

    .ant-picker-clear {
      background: ${colors.SECONDARY_400};
      color: ${colors.NEUTRAL_50};
    }
  `

  // Select dropdown styles
  const selectStyles = css`
    .ant-select-selector {
      background-color: ${colors.SECONDARY_400} !important;
      border: 1px solid ${colors.SECONDARY_200} !important;
      border-radius: 8px !important;
      min-height: 40px !important;
      padding: 4px 8px !important;
      color: ${colors.NEUTRAL_0} !important;
    }

    .ant-select-selection-item {
      line-height: 30px !important;
      color: ${colors.NEUTRAL_0} !important;
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 500;
    }

    .ant-select-selection-placeholder {
      color: ${colors.NEUTRAL_50} !important;
      line-height: 30px !important;
    }

    .ant-select-arrow {
      color: ${colors.NEUTRAL_0};
    }

    /* Multi-select tag styles */
    .ant-select-selection-item {
      background-color: ${colors.SECONDARY_300} !important;
      border: 1px solid ${colors.SECONDARY_200} !important;
      border-radius: 4px !important;
      color: ${colors.NEUTRAL_0} !important;
      height: 28px !important;
      line-height: 26px !important;
    }

    .ant-select-selection-item-remove {
      color: ${colors.NEUTRAL_50} !important;
      &:hover {
        color: ${colors.NEUTRAL_0} !important;
      }
    }

    &:hover .ant-select-selector {
      border-color: ${colors.PRIMARY_300} !important;
    }

    &.ant-select-focused .ant-select-selector {
      border-color: ${colors.PRIMARY_300} !important;
      box-shadow: 0 0 0 2px rgba(242, 180, 0, 0.2) !important;
    }

    .ant-select-clear {
      background: ${colors.SECONDARY_400};
      color: ${colors.NEUTRAL_50};
    }
  `

  const iconButtonStyles = css`
    background: transparent;
    border: none;
    color: ${colors.PRIMARY_300};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;

    &:hover {
      color: ${colors.PRIMARY_200};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `

  if (isEditing && editState) {
    // Edit mode
    return (
      <>
        <Global
          styles={css`
            .dark-select-popup.ant-select-dropdown,
            .dark-select-popup .ant-select-dropdown {
              background-color: ${colors.SECONDARY_400} !important;
              border: 1px solid ${colors.SECONDARY_200} !important;
            }

            .dark-select-popup .ant-select-item,
            .dark-select-popup .ant-select-item-option-content {
              color: ${colors.WHITE} !important;
            }

            .dark-select-popup
              .ant-select-item-option-active:not(
                .ant-select-item-option-disabled
              ) {
              background-color: ${colors.SECONDARY_300} !important;
            }

            .dark-select-popup
              .ant-select-item-option-selected:not(
                .ant-select-item-option-disabled
              ) {
              background-color: ${colors.PRIMARY_300} !important;
            }

            .dark-select-popup
              .ant-select-item-option-selected:not(
                .ant-select-item-option-disabled
              )
              .ant-select-item-option-content {
              color: ${colors.SECONDARY_500} !important;
            }

            .${TIME_SLOT_ROW_PICKER_CLASS} {
              .ant-picker-panel-container {
                background-color: ${colors.SECONDARY_300};
                border: 1px solid ${colors.SECONDARY_200};
                border-radius: 8px;
                overflow: hidden;
              }

              .ant-picker-panel {
                background-color: ${colors.SECONDARY_300};
                border: none;
              }

              .ant-picker-header {
                color: ${colors.WHITE};
                border-bottom: 1px solid ${colors.SECONDARY_200};
              }

              .ant-picker-content {
                li {
                  color: ${colors.NEUTRAL_50};

                  &:hover {
                    background-color: ${colors.SECONDARY_400};
                  }
                }

                .ant-picker-time-panel-cell-selected
                  .ant-picker-time-panel-cell-inner {
                  background-color: ${colors.PRIMARY_300} !important;
                  color: ${colors.SECONDARY_300} !important;
                }
              }

              .ant-picker-footer {
                border-top: 1px solid ${colors.SECONDARY_200};
              }

              .ant-picker-ok button {
                background-color: ${colors.PRIMARY_300};
                border-color: ${colors.PRIMARY_300};
                color: ${colors.SECONDARY_300};
              }
            }
          `}
        />
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 8px 16px 8px 48px;
            background-color: ${colors.SECONDARY_500};
            width: 100%;
          `}
        >
          {/* Slot number */}
          <span
            css={css`
              ${textStyles}
              width: ${COLUMN_WIDTHS.SLOT}px;
              color: ${colors.NEUTRAL_0};
              font-weight: 400;
            `}
          >
            {slotNumber}
          </span>

          {/* Frequency dropdown */}
          <Select
            value={editState.frequency}
            onChange={(value) =>
              onEditStateChange({ ...editState, frequency: value })
            }
            disabled={loading}
            css={selectStyles}
            style={{ width: COLUMN_WIDTHS.FREQUENCY }}
            dropdownStyle={{ backgroundColor: colors.SECONDARY_400 }}
            options={RECURRENCE_TYPES.map((rt) => ({
              value: rt.value,
              label: rt.label,
            }))}
          />

          {/* Date - not editable for weekly recurrence */}
          <span
            css={css`
              ${textStyles}
              width: ${COLUMN_WIDTHS.DATE}px;
              color: ${colors.NEUTRAL_0};
              font-weight: 400;
            `}
          >
            ---
          </span>

          {/* Days of week selector */}
          <div
            css={css`
              width: ${COLUMN_WIDTHS.DAYS}px;
            `}
          >
            <DayOfWeekSelector
              selectedDays={editState.days}
              onChange={(days) => onEditStateChange({ ...editState, days })}
              disabled={loading}
            />
          </div>

          {/* Start time */}
          <div
            css={css`
              width: ${COLUMN_WIDTHS.START_TIME}px;
            `}
          >
            <TimePicker
              value={
                editState.startTime ? dayjs(editState.startTime, 'HH:mm') : null
              }
              onChange={(value) =>
                onEditStateChange({
                  ...editState,
                  startTime: value
                    ? value.format('HH:mm')
                    : editState.startTime,
                })
              }
              format='HH:mm'
              disabled={loading}
              css={timePickerStyles}
              minuteStep={5}
              showNow={false}
              needConfirm={false}
              popupClassName={TIME_SLOT_ROW_PICKER_CLASS}
              style={{ width: '100%' }}
            />
          </div>

          {/* End time */}
          <div
            css={css`
              width: ${COLUMN_WIDTHS.END_TIME}px;
            `}
          >
            <TimePicker
              value={
                editState.endTime ? dayjs(editState.endTime, 'HH:mm') : null
              }
              onChange={(value) =>
                onEditStateChange({
                  ...editState,
                  endTime: value ? value.format('HH:mm') : editState.endTime,
                })
              }
              format='HH:mm'
              disabled={loading}
              css={timePickerStyles}
              minuteStep={5}
              showNow={false}
              needConfirm={false}
              popupClassName={TIME_SLOT_ROW_PICKER_CLASS}
              style={{ width: '100%' }}
            />
          </div>

          {/* Duration (calculated) */}
          <span
            css={css`
              ${textStyles}
              width: ${COLUMN_WIDTHS.DURATION}px;
            `}
          >
            {calculateDuration(editState.startTime, editState.endTime)}
          </span>

          {/* Divisions dropdown */}
          <div
            css={css`
              width: ${COLUMN_WIDTHS.DIVISIONS}px;
            `}
          >
            <Select
              ref={divisionsSelectRef}
              mode='multiple'
              value={editState.divisions}
              onChange={(values: string[]) =>
                onEditStateChange({ ...editState, divisions: values })
              }
              placeholder='All Divisions'
              disabled={loading}
              css={selectStyles}
              style={{ width: '100%' }}
              maxTagCount='responsive'
              allowClear
              popupClassName='dark-select-popup'
              dropdownRender={(menu) => (
                <>
                  <div
                    css={css`
                      padding: 8px 12px;
                      cursor: pointer;
                      color: ${colors.PRIMARY_300};
                      border-bottom: 1px solid ${colors.SECONDARY_200};
                      &:hover {
                        background-color: ${colors.SECONDARY_400};
                      }
                    `}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const allIds = divisions.map((d) => d.id)
                      const allSelected =
                        editState.divisions.length === divisions.length
                      onEditStateChange({
                        ...editState,
                        divisions: allSelected ? [] : allIds,
                      })
                      // Close dropdown after selecting all
                      if (!allSelected) {
                        divisionsSelectRef.current?.blur()
                      }
                    }}
                  >
                    All Divisions
                  </div>
                  {menu}
                </>
              )}
            >
              {divisions.map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.name}
                </Option>
              ))}
            </Select>
          </div>

          {/* Cancel button */}
          <button
            type='button'
            onClick={onCancel}
            disabled={loading}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 4px 16px;
              background-color: transparent;
              border: 1px solid ${colors.NEUTRAL_100};
              border-radius: 8px;
              color: ${colors.NEUTRAL_50};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 500;
              line-height: 20.3px;
              cursor: pointer;
              transition: background-color 0.2s;

              &:hover:not(:disabled) {
                background-color: ${colors.SECONDARY_200};
              }

              &:disabled {
                opacity: 0.7;
                cursor: not-allowed;
              }
            `}
          >
            Cancel
          </button>

          {/* Save button */}
          <button
            type='button'
            onClick={onSave}
            disabled={loading}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 4px 16px 4px 8px;
              background-color: ${colors.PRIMARY_300};
              border: none;
              border-radius: 8px;
              color: ${colors.SECONDARY_300};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 500;
              line-height: 20.3px;
              cursor: pointer;
              transition: background-color 0.2s;

              &:hover:not(:disabled) {
                background-color: ${colors.PRIMARY_200};
              }

              &:disabled {
                opacity: 0.7;
                cursor: not-allowed;
              }
            `}
          >
            <Icon name='plus' fontSize={24} color={colors.SECONDARY_300} />
            Save
          </button>
        </div>
      </>
    )
  }

  // Display mode
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 16px 8px 48px;
        background-color: ${colors.SECONDARY_500};
        width: 100%;

        &:hover {
          background-color: ${colors.SECONDARY_400};
        }
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;
        `}
      >
        {/* Slot number */}
        <span
          css={css`
            ${textStyles}
            width: ${COLUMN_WIDTHS.SLOT}px;
            color: ${colors.NEUTRAL_0};
            font-weight: 400;
          `}
        >
          {slotNumber}
        </span>

        {/* Frequency */}
        <span
          css={css`
            ${textStyles}
            width: ${COLUMN_WIDTHS.FREQUENCY}px;
          `}
        >
          {RECURRENCE_TYPES.find((rt) => rt.value === timeSlot.recurrence_type)
            ?.label || 'Weekly'}
        </span>

        {/* Date */}
        <span
          css={css`
            ${textStyles}
            width: ${COLUMN_WIDTHS.DATE}px;
            color: ${colors.NEUTRAL_0};
            font-weight: 400;
          `}
        >
          ---
        </span>

        {/* Days of week */}
        <span
          css={css`
            ${textStyles}
            width: ${COLUMN_WIDTHS.DAYS}px;
          `}
        >
          {formatDaysDisplay(timeSlot.days)}
        </span>

        {/* Start time */}
        <span
          css={css`
            ${textStyles}
            width: ${COLUMN_WIDTHS.START_TIME}px;
          `}
        >
          {formatTimeDisplay(times.start)}
        </span>

        {/* End time */}
        <span
          css={css`
            ${textStyles}
            width: ${COLUMN_WIDTHS.END_TIME}px;
          `}
        >
          {formatTimeDisplay(times.end)}
        </span>

        {/* Duration */}
        <span
          css={css`
            ${textStyles}
            width: ${COLUMN_WIDTHS.DURATION}px;
          `}
        >
          {calculateDuration(times.start, times.end)}
        </span>

        {/* Divisions */}
        <span
          css={css`
            ${textStyles}
            width: ${COLUMN_WIDTHS.DIVISIONS}px;
          `}
          title={
            !timeSlot.divisions || timeSlot.divisions.length === 0
              ? 'All Divisions'
              : timeSlot.divisions
                  .map((id) => divisions.find((d) => d.id === id)?.name || id)
                  .join(', ')
          }
        >
          {!timeSlot.divisions || timeSlot.divisions.length === 0
            ? 'All Divisions'
            : timeSlot.divisions.length === divisions.length
            ? 'All Divisions'
            : timeSlot.divisions
                .map((id) => divisions.find((d) => d.id === id)?.name || id)
                .join(', ')}
        </span>
      </div>

      {/* Action icons */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;
        `}
      >
        <button
          type='button'
          onClick={onEdit}
          css={iconButtonStyles}
          aria-label='Edit time slot'
        >
          <EditOutlined style={{ fontSize: 18 }} />
        </button>
        <button
          type='button'
          onClick={onDelete}
          css={css`
            ${iconButtonStyles}
            color: ${colors.ERROR_200};

            &:hover {
              color: ${colors.ERROR_300};
            }
          `}
          aria-label='Delete time slot'
        >
          <DeleteOutlined style={{ fontSize: 18 }} />
        </button>
      </div>
    </div>
  )
}

export default TimeSlotRow
